// client/src/pages/MessagesPage.js
import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { AuthContext } from '../context/AuthContext';
import {
  Box, Typography, List, ListItemButton, ListItemText, Divider, Grid, TextField, Button, Stack, Paper, Chip,
} from '@mui/material';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import io from 'socket.io-client';
import EmptyState from '../components/EmptyState';
import { API_BASE_URL } from '../utils/media';

// In-house messaging: buyer <-> seller, or buyer <-> support (admin). No
// third-party chat widget — just the app's own WebSocket + REST endpoints.
const MessagesPage = () => {
  const { token, user } = useContext(AuthContext);
  const [conversations, setConversations] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const socketRef = useRef(null);
  const bottomRef = useRef(null);

  const fetchConversations = useCallback(async () => {
    if (!token) return;
    const response = await fetch(`${API_BASE_URL}/api/conversations`, { headers: { Authorization: `Bearer ${token}` } });
    if (response.ok) {
      const data = await response.json();
      setConversations(data);
      if (!activeId && data.length > 0) setActiveId(data[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { fetchConversations(); }, [fetchConversations]);

  const fetchMessages = useCallback(async () => {
    if (!activeId || !token) return;
    const response = await fetch(`${API_BASE_URL}/api/conversations/${activeId}/messages`, { headers: { Authorization: `Bearer ${token}` } });
    if (response.ok) {
      const data = await response.json();
      setMessages(data.messages);
    }
  }, [activeId, token]);

  useEffect(() => { fetchMessages(); }, [fetchMessages]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  useEffect(() => {
    const socket = io(`${API_BASE_URL}`);
    socketRef.current = socket;
    socket.on('newMessage', ({ conversationId }) => {
      if (conversationId === activeId) fetchMessages();
      fetchConversations();
    });
    return () => socket.disconnect();
  }, [activeId, fetchMessages, fetchConversations]);

  const handleSend = async () => {
    if (!draft.trim() || !activeId) return;
    const body = draft.trim();
    setDraft('');
    const response = await fetch(`${API_BASE_URL}/api/conversations/${activeId}/messages`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ body }),
    });
    if (response.ok) fetchMessages();
  };

  const activeConvo = conversations.find((c) => c.id === activeId);

  if (conversations.length === 0) {
    return (
      <EmptyState
        icon={<ForumOutlinedIcon />}
        title="No conversations yet"
        description="Messages you start with a seller (from their store page) or with support will show up here."
      />
    );
  }

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} gutterBottom>Messages</Typography>
      <Grid container sx={{ border: 1, borderColor: 'divider', borderRadius: 1, minHeight: 480 }}>
        <Grid item xs={12} sm={4} sx={{ borderRight: { sm: 1 }, borderColor: 'divider' }}>
          <List disablePadding>
            {conversations.map((c) => (
              <ListItemButton key={c.id} selected={c.id === activeId} onClick={() => setActiveId(c.id)}>
                <ListItemText
                  primary={c.subject}
                  secondary={c.seller_id ? (c.buyer_id === user?.userId ? c.seller_email : c.buyer_email) : 'Support'}
                />
                {c.unread_count > 0 && <Chip label={c.unread_count} size="small" color="secondary" />}
              </ListItemButton>
            ))}
          </List>
        </Grid>
        <Grid item xs={12} sm={8} sx={{ display: 'flex', flexDirection: 'column' }}>
          {activeConvo && (
            <>
              <Box sx={{ p: 2 }}>
                <Typography fontWeight={700}>{activeConvo.subject}</Typography>
                <Typography variant="caption" color="text.secondary">
                  With {activeConvo.seller_id ? (activeConvo.buyer_id === user?.userId ? activeConvo.seller_email : activeConvo.buyer_email) : 'Support'}
                </Typography>
              </Box>
              <Divider />
              <Box sx={{ flexGrow: 1, p: 2, overflowY: 'auto', maxHeight: 340 }}>
                <Stack spacing={1.5}>
                  {messages.map((m) => (
                    <Paper
                      key={m.id} variant="outlined"
                      sx={{
                        p: 1.5, maxWidth: '75%', alignSelf: m.sender_id === user?.userId ? 'flex-end' : 'flex-start',
                        bgcolor: m.sender_id === user?.userId ? 'primary.main' : 'background.paper',
                        color: m.sender_id === user?.userId ? 'primary.contrastText' : 'text.primary',
                      }}
                    >
                      <Typography variant="body2">{m.body}</Typography>
                      <Typography variant="caption" sx={{ opacity: 0.7 }}>{new Date(m.created_at).toLocaleString()}</Typography>
                    </Paper>
                  ))}
                  <div ref={bottomRef} />
                </Stack>
              </Box>
              <Divider />
              <Stack direction="row" spacing={1} sx={{ p: 2 }}>
                <TextField
                  size="small" fullWidth placeholder="Type a message..." value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleSend(); }}
                />
                <Button variant="contained" onClick={handleSend} disabled={!draft.trim()}>Send</Button>
              </Stack>
            </>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default MessagesPage;
